import Box from 'components/shared/Box'
import Text from 'components/shared/Text'
import moment from 'moment'
import { useTicketPromotions } from 'routes/Tickets/utils/useTicketPromotions'

export interface CustomMonthNameProps {
    month: string
    year: number
    hasDarkMode?: boolean
}

const CustomMonthName = ({
    month,
    year,
    hasDarkMode,
}: CustomMonthNameProps) => (
    <Box isFullWidth direction="row" wrap="nowrap" justify="center" gap={8}>
        <Text
            variant="heading4"
            color={hasDarkMode ? 'grayscale_500' : undefined}
        >
            {month}
        </Text>
        <Text
            variant="heading4"
            color={hasDarkMode ? 'grayscale_200' : 'grayscale_700'}
        >
            {year}
        </Text>
    </Box>
)

export interface CustomMonthStandardProps {
    month: string
    year: number
    hasDarkMode?: boolean
}

const CustomMonthStandard = ({
    month,
    year,
    hasDarkMode,
}: CustomMonthStandardProps) => (
    <Box direction="row" gap={8} align="center" justify="center">
        <CustomMonthName month={month} year={year} hasDarkMode={hasDarkMode} />
    </Box>
)

export interface CustomMonthPromoProps {
    monthName: string
    monthIndex: number
    year: number
}

const CustomMonthPromo = ({
    monthName,
    monthIndex,
    year,
}: CustomMonthPromoProps) => {
    const { renderActivePromotions } = useTicketPromotions()
    return (
        <Box
            direction={['column', 'row']}
            gap={8}
            align="center"
            justify="center"
        >
            <CustomMonthName month={monthName} year={year} hasDarkMode />
            {renderActivePromotions<Omit<CustomMonthPromoProps, 'year'>>({
                key: 'calendar',
                props: { monthIndex, monthName },
            })}
        </Box>
    )
}

export interface CustomMonthProps {
    month: moment.Moment
    onMonthSelect?: (currentMonth: moment.Moment, newMonthVal: string) => void
    onYearSelect?: (currentMonth: moment.Moment, newMonthVal: string) => void
    isVisible?: boolean
    hasDarkMode?: boolean
    hasAdditionalInfo?: boolean
}

const CustomMonth = ({
    month,
    hasDarkMode,
    hasAdditionalInfo,
}: CustomMonthProps) => {
    const monthName = moment(month).format('MMMM')
    const monthNameStandalone =
        monthName[0].toUpperCase() + monthName.substring(1)
    const monthYear = month.year()

    return hasAdditionalInfo ? (
        <CustomMonthPromo
            monthName={monthNameStandalone}
            monthIndex={month.month()}
            year={monthYear}
        />
    ) : (
        <CustomMonthStandard
            month={monthNameStandalone}
            year={monthYear}
            hasDarkMode={hasDarkMode}
        />
    )
}

export default CustomMonth
